
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Search, Filter, X, SortAsc, SortDesc } from "lucide-react";
import { EtsyProduct } from "@/hooks/useEtsyProducts";

export interface ProductFilters {
  search: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  priceRange: { min: number; max: number };
  stockStatus: string;
  state: string;
  salesRange: { min: number; max: number };
}

interface ProductFiltersProps {
  products: EtsyProduct[];
  filters: ProductFilters;
  onFiltersChange: (filters: ProductFilters) => void;
  onClearFilters: () => void;
}

const ProductFiltersComponent = ({ products, filters, onFiltersChange, onClearFilters }: ProductFiltersProps) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const maxPrice = Math.max(...products.map(p => p.price || 0));
  const maxSales = Math.max(...products.map(p => p.salesCount || 0));

  const updateFilter = (key: keyof ProductFilters, value: any) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const sortOptions = [
    { value: 'title', label: 'Product Name' },
    { value: 'price', label: 'Price' },
    { value: 'quantity', label: 'Stock Quantity' },
    { value: 'salesCount', label: 'Sales Count' },
    { value: 'createdAt', label: 'Date Added' },
    { value: 'state', label: 'Status' },
  ];

  const stockStatusOptions = [
    { value: 'all', label: 'All Stock Levels' },
    { value: 'in-stock', label: 'In Stock (6+)' },
    { value: 'low-stock', label: 'Low Stock (1-5)' },
    { value: 'out-of-stock', label: 'Out of Stock (0)' },
    { value: 'unknown', label: 'Unknown Stock' },
  ];

  const stateOptions = [
    { value: 'all', label: 'All States' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'draft', label: 'Draft' },
  ];

  const hasActiveFilters = filters.search || 
    filters.sortBy !== 'title' || 
    filters.sortOrder !== 'asc' ||
    filters.priceRange.min > 0 || 
    filters.priceRange.max < maxPrice ||
    filters.stockStatus !== 'all' ||
    filters.state !== 'all' ||
    filters.salesRange.min > 0 ||
    filters.salesRange.max < maxSales;

  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Product Filters
          </CardTitle>
          <div className="flex items-center gap-2">
            {hasActiveFilters && (
              <Button variant="outline" size="sm" onClick={onClearFilters}>
                <X className="h-4 w-4 mr-1" />
                Clear All
              </Button>
            )}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              {showAdvanced ? 'Simple' : 'Advanced'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Search products by name or description..."
            value={filters.search}
            onChange={(e) => updateFilter('search', e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Sort */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="sort-by">Sort By</Label>
            <Select value={filters.sortBy} onValueChange={(value) => updateFilter('sortBy', value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sortOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="sort-order">Sort Order</Label>
            <Select value={filters.sortOrder} onValueChange={(value: 'asc' | 'desc') => updateFilter('sortOrder', value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="asc">
                  <div className="flex items-center gap-2">
                    <SortAsc className="h-4 w-4" />
                    Ascending
                  </div>
                </SelectItem>
                <SelectItem value="desc">
                  <div className="flex items-center gap-2">
                    <SortDesc className="h-4 w-4" />
                    Descending
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="stock-status">Stock Status</Label>
            <Select value={filters.stockStatus} onValueChange={(value) => updateFilter('stockStatus', value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {stockStatusOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {showAdvanced && (
          <div className="space-y-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Price Range */}
              <div>
                <Label className="text-sm font-medium">Price Range</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <div>
                    <Label htmlFor="price-min" className="text-xs text-gray-500">Min</Label>
                    <Input
                      id="price-min"
                      type="number"
                      placeholder="0"
                      value={filters.priceRange.min || ''}
                      onChange={(e) => updateFilter('priceRange', { 
                        ...filters.priceRange, 
                        min: Number(e.target.value) || 0 
                      })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="price-max" className="text-xs text-gray-500">Max</Label>
                    <Input
                      id="price-max"
                      type="number"
                      placeholder={maxPrice.toString()}
                      value={filters.priceRange.max === maxPrice ? '' : filters.priceRange.max}
                      onChange={(e) => updateFilter('priceRange', { 
                        ...filters.priceRange, 
                        max: Number(e.target.value) || maxPrice 
                      })}
                    />
                  </div>
                </div>
              </div>

              {/* Sales Range */}
              <div>
                <Label className="text-sm font-medium">Sales Count Range</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <div>
                    <Label htmlFor="sales-min" className="text-xs text-gray-500">Min</Label>
                    <Input
                      id="sales-min"
                      type="number"
                      placeholder="0"
                      value={filters.salesRange.min || ''}
                      onChange={(e) => updateFilter('salesRange', { 
                        ...filters.salesRange, 
                        min: Number(e.target.value) || 0 
                      })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="sales-max" className="text-xs text-gray-500">Max</Label>
                    <Input
                      id="sales-max"
                      type="number"
                      placeholder={maxSales.toString()}
                      value={filters.salesRange.max === maxSales ? '' : filters.salesRange.max}
                      onChange={(e) => updateFilter('salesRange', { 
                        ...filters.salesRange, 
                        max: Number(e.target.value) || maxSales 
                      })}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* State Filter */}
            <div>
              <Label htmlFor="state">Product State</Label>
              <Select value={filters.state} onValueChange={(value) => updateFilter('state', value)}>
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {stateOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="pt-3 border-t border-gray-100">
            <div className="flex flex-wrap gap-2">
              {filters.search && (
                <Badge variant="secondary">Search: "{filters.search}"</Badge>
              )}
              {filters.sortBy !== 'title' && (
                <Badge variant="secondary">
                  Sort: {sortOptions.find(o => o.value === filters.sortBy)?.label} ({filters.sortOrder})
                </Badge>
              )}
              {filters.stockStatus !== 'all' && (
                <Badge variant="secondary">
                  Stock: {stockStatusOptions.find(o => o.value === filters.stockStatus)?.label}
                </Badge>
              )}
              {filters.state !== 'all' && (
                <Badge variant="secondary">
                  State: {stateOptions.find(o => o.value === filters.state)?.label}
                </Badge>
              )}
              {(filters.priceRange.min > 0 || filters.priceRange.max < maxPrice) && (
                <Badge variant="secondary">
                  Price: ${filters.priceRange.min} - ${filters.priceRange.max}
                </Badge>
              )}
              {(filters.salesRange.min > 0 || filters.salesRange.max < maxSales) && (
                <Badge variant="secondary">
                  Sales: {filters.salesRange.min} - {filters.salesRange.max}
                </Badge>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ProductFiltersComponent;
